/**
 * `<StrikeMeter>` — the "am I winning" glance (components.md §2.2).
 *
 * A horizontal meter showing the live index relative to the **fixed** strike.
 * *Fixed* is the word, and it is not a synonym for what a perps UI would call
 * this: the strike is written once at the `FIXING → OPEN` transition and never
 * again, while trading continues to the expiry instant. Nothing here stops.
 *
 * Three things here are load-bearing and easy to get wrong:
 *
 * **The ±5bp floor does real work.** The scale auto-ranges to ±(3 × the trailing
 * **60-second** realised range) — one market period, not five — floored at ±5bp.
 * At a 60-second horizon the typical excursion is a handful of basis points, so
 * unfloored the marker slams the edges on a quiet minute and the meter reads as
 * pure noise. The floor is what makes a flat market look flat.
 *
 * **The tie hairline is on the UP side only.** `settle >= strike` resolves UP
 * (D5), so the boundary is genuinely asymmetric, and this meter is the only
 * place in the product a user can see that. One pixel, on one side, with a
 * tooltip that says so.
 *
 * **The strike is read as an exact observation, not a float.** `impliedOutcome`
 * compares `raw` at equal `expo`; `toNumber` exists only to place a marker. The
 * exponent comes from the payload and is 8 *or* 18 per stream (`docs/05`) — the
 * mock serves BTC and ETH at different scales precisely so a client that assumed
 * one would be visibly wrong about the other.
 */

import { useEffect, useRef, useState } from 'react'
import { Glyph, Tooltip } from '@/components/primitives'
import { cx } from '@/lib/cx'
import {
  TrailingRange,
  bpsBetween,
  meterHalfRangeBps,
  meterPosition,
  observationFrom,
  toNumber,
  type Observation,
} from '@/domain/price'
import { observationTag } from '@/domain/resolution'
import { formatUtcTime } from '@/lib/format'

export interface StrikeMeterProps {
  /** The `strike` object off `market.{id}`. `null` while `SCHEDULED`/`FIXING`,
   *  and — under `PRINT_AT_V1` — for the first second or two of `OPEN`. */
  strike: unknown
  /**
   * The anchor, when the market is `OPEN` and its strike has not landed yet
   * (register §F73). The strike is the print stamped at this instant and it is
   * on its way, so the corner says which print is coming rather than leaving a
   * dash that reads as a fault.
   */
  pendingAtMs?: number | null
  /** The latest `index.{symbol}` payload. */
  index: unknown
  /** Corrected server time, for the trailing window. */
  nowMs: number
  /** Decimal places for the strike label — `config.symbols[].price_decimals`. */
  priceDecimals?: number
  stale?: boolean
  className?: string
}

export function StrikeMeter({
  strike,
  index,
  nowMs,
  priceDecimals = 2,
  stale,
  className,
  pendingAtMs = null,
}: StrikeMeterProps) {
  const strikeObs = observationFrom(strike)
  // The strike carries its own `window_start_ms`/`window_end_ms`, so the label
  // needs no extra prop — and a prop the caller had to source from somewhere is
  // how the strike window and the settlement window get mixed up.
  const strikeTag = observationTag(strike)
  const pending = strikeObs === null && typeof pendingAtMs === 'number'
  const indexObs = observationFrom(index)
  const strikeValue = toNumber(strikeObs)
  const indexValue = toNumber(indexObs)

  // 60 seconds of index samples — one market period, per §2.2.
  //
  // The window is a ref because pushing a sample must not itself cause a render,
  // but the *derived* half-range is state: the scale is a rendered value, and
  // reading a ref during render is exactly the pattern that makes a component
  // paint a number one tick behind the one it was given.
  const trailing = useRef<TrailingRange | null>(null)
  const [halfRangeBps, setHalfRangeBps] = useState(meterHalfRangeBps(null))

  useEffect(() => {
    trailing.current ??= new TrailingRange(60_000)
    if (indexValue === null) return
    trailing.current.push(nowMs, indexValue)
    const next = meterHalfRangeBps(trailing.current.rangeBps(strikeValue))
    // Only when it actually moves. The range is stable for most of a quiet
    // minute and re-rendering the meter five times a second to write the same
    // number is the churn the floor exists to prevent.
    setHalfRangeBps((held) => (Math.abs(held - next) < 0.01 ? held : next))
  }, [indexValue, nowMs, strikeValue])

  const distanceBps = bpsBetween(indexValue, strikeValue)
  const position = meterPosition(distanceBps, halfRangeBps)
  const delta = indexValue === null || strikeValue === null ? null : indexValue - strikeValue
  const up = (distanceBps ?? 0) >= 0

  return (
    <div
      data-stale={stale ? 'true' : undefined}
      className={cx(
        'flex flex-col gap-1_5 data-[stale=true]:opacity-55 data-[stale=true]:saturate-50',
        className,
      )}
    >
      {/* The signed delta, above the meter. Glyph *and* sign *and* colour —
          never colour alone (design-system §2.5). */}
      <div className="flex items-baseline justify-between gap-2">
        <span
          className={cx(
            'ud-num text-body-sm',
            delta === null ? 'text-text-tertiary' : up ? 'text-up-text' : 'text-down-text',
          )}
        >
          {delta === null ? (
            '—'
          ) : (
            <>
              <Glyph name={up ? 'up' : 'down'} />{' '}
              {signedFixed(delta, priceDecimals)}
              <span className="text-text-tertiary">
                {' '}
                ({signedFixed(distanceBps ?? 0, 2)}bp)
              </span>
            </>
          )}
        </span>
        <span className="text-micro text-text-tertiary">
          ±{halfRangeBps.toFixed(1)}bp
        </span>
      </div>

      <div
        className="relative h-4 w-full rounded-xs bg-bg-inset"
        role="meter"
        aria-valuemin={-halfRangeBps}
        aria-valuemax={halfRangeBps}
        aria-valuenow={distanceBps ?? 0}
        aria-label="index price relative to strike"
      >
        {/* The fill extends from the centre towards the marker. 16% alpha, so
            the marker and the tick both stay legible on top of it. */}
        {distanceBps === null ? null : (
          <span
            aria-hidden="true"
            className={cx('absolute inset-y-0 opacity-[0.16]', up ? 'bg-up-solid' : 'bg-down-solid')}
            style={{
              left: `${Math.min(50, position * 100)}%`,
              width: `${Math.abs(position * 100 - 50)}%`,
            }}
          />
        )}

        {/* The centre tick, and the tie hairline immediately to its **UP**
            side. One hover target carries both, because they are one fact:
            `settle >= strike` resolves UP, so the boundary is one pixel wide
            and it belongs to UP. */}
        <Tooltip
          content="ties resolve UP"
          className="absolute inset-y-0 left-1/2 w-2 -translate-x-1/2 justify-center"
        >
          <span aria-hidden="true" className="absolute inset-y-0 left-1/2 w-px bg-border-strong" />
          <span
            aria-hidden="true"
            className="absolute inset-y-0 left-[calc(50%+1px)] w-px bg-up-solid"
          />
        </Tooltip>

        {distanceBps === null ? null : (
          <span
            aria-hidden="true"
            className={cx(
              'absolute inset-y-0 w-0_5 -translate-x-1/2',
              up ? 'bg-up-solid' : 'bg-down-solid',
            )}
            style={{ left: `${position * 100}%` }}
          />
        )}
      </div>

      {/* The strike, labelled with the print it is. A user comparing it to a
          spot ticker will otherwise think it is wrong. */}
      <div className="flex items-baseline justify-between gap-2">
        <span className="ud-num text-caption text-text-secondary">
          {strikeObs ? formatObservation(strikeObs, priceDecimals) : pending ? 'strike forming' : '—'}
        </span>
        {/* Read off the strike's **own** observation, not the settlement's.
            Empty while there is no strike — a label with nothing to label is
            worse than no label — except while the strike print is on its way,
            when it names the print that will fix it (§F73). */}
        <span className="text-micro text-text-tertiary">
          {strikeTag || (pending ? `print at ${formatUtcTime(pendingAtMs)}` : '')}
        </span>
      </div>
    </div>
  )
}

/**
 * The observation's own decimal string, trimmed for display.
 *
 * Sliced rather than re-derived from `raw`: `value` is what the server published
 * and display tolerates a last-digit error, while the ledger does not and is not
 * ours (`lib/format` header).
 */
function formatObservation(observation: Observation, dp: number): string {
  const [whole = '0', frac = ''] = observation.value.split('.')
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  return dp === 0 ? `$${grouped}` : `$${grouped}.${frac.slice(0, dp).padEnd(dp, '0')}`
}

function signedFixed(value: number, dp: number): string {
  return `${value < 0 ? '-' : '+'}${Math.abs(value).toFixed(dp)}`
}
